import { useDispatch, useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import { pluralizeIssues } from '../../../../shared/helpers';
import type { ResolutionReason, VulnIgnoreReason } from '../../../../shared/issuesV2';
import {
  Category,
  ExceptionIgnoreScope,
  ExceptionPackageScope,
  IgnoredKind,
  VulnIgnoreReasons,
} from '../../../../shared/issuesV2';
import { useAddToast } from '../../../contexts/ToastContext';
import * as IssuesV2Store from '../../../redux/issuesV2';
import IgnoreWithExceptionsModal from './IgnoreWithExceptionsModal';
import useIssueExceptions from '../IssueExceptions/useIssueExceptions';

type IssueExceptionOnIgnore = {
  ignoredKind: IgnoredKind.IssueException;
  resolutionReason?: ResolutionReason;
  notes?: string;
  ignoreScope: ExceptionIgnoreScope;
  packageScope: ExceptionPackageScope;
  expiresAfter: string | null;
  packageLabelIds?: number[];
};

export type IgnoreModalOnIgnoreParams =
  | {
      ignoredKind: Omit<IgnoredKind, 'issueException'>;
      resolutionReason?: ResolutionReason;
      notes?: string;
      expiresAfter?: string | null;
      packageLabelIds?: number[];
    }
  | IssueExceptionOnIgnore;

const isIssueExceptionIgnore = (ignoreScope: IgnoreModalOnIgnoreParams): ignoreScope is IssueExceptionOnIgnore =>
  ignoreScope.ignoredKind === IgnoredKind.IssueException;

/**
 * Check if an issue exception action qualifies as a simple ignore:
 * 1. Affects a project or release group
 * 2. Affects a specific version
 * 3. Does not have an expiration date
 */
const isSimpleIgnoreAction = (action: IssueExceptionOnIgnore): boolean => {
  // must be scoped to a Project or ReleaseGroup
  if (![ExceptionIgnoreScope.Project, ExceptionIgnoreScope.ReleaseGroup].includes(action.ignoreScope)) {
    return false;
  }
  // must not have any package labels
  if (action.packageLabelIds && action.packageLabelIds.length > 0) {
    return false;
  }

  // must not have an expiration date
  if (action.expiresAfter) {
    return false;
  }

  // must only affect a specific package version
  return action.packageScope === ExceptionPackageScope.SelectedVersion;
};

type IgnoreIssuesModalBaseProps = {
  show: boolean;
  selectedIssuesCount: number;
  onHide: () => void;
};

/**
 * Container component for the ignore modal for use on the IssuesInbox.
 * For the inbox, after ignoring we need to re-fetch the issues and the status summary.
 */
export const IgnoreIssuesModalInboxContainer: VoidFunctionComponent<IgnoreIssuesModalBaseProps> = ({
  show,
  selectedIssuesCount,
  onHide,
}) => {
  const addToast = useAddToast();
  const dispatch = useDispatch();
  const issueCategory = useSelector(IssuesV2Store.selectors.getIssueCategory);
  const areVulnerabilityIssues = issueCategory === Category.Vulnerability;

  const onIgnore = async (scope: IgnoreModalOnIgnoreParams) => {
    const shouldIncludeReason =
      areVulnerabilityIssues &&
      scope.resolutionReason &&
      Object.values(VulnIgnoreReasons).includes(scope.resolutionReason);

    const reason = shouldIncludeReason ? (scope.resolutionReason as VulnIgnoreReason) : undefined;

    // Perform the IssueExceptions action if the scope has the proper typing and
    // the action isn't a simple ignore.
    if (isIssueExceptionIgnore(scope) && !isSimpleIgnoreAction(scope)) {
      dispatch(
        IssuesV2Store.actions.createIssueExceptionsForSelectedIssues(
          scope.ignoreScope,
          scope.packageScope,
          scope.expiresAfter,
          scope.notes,
          reason,
          scope.packageLabelIds,
        ),
      );
      // Default case to perform normal Issues V2 ignoring if it's not IssueExceptions.
    } else {
      dispatch(IssuesV2Store.actions.ignoreSelectedIssues(scope.notes, reason));
    }

    onHide();
    //  Display a toast letting the user know the action was successful.
    addToast({
      type: 'success',
      content: `Ignored ${pluralizeIssues(selectedIssuesCount)}.`,
    });
  };

  return (
    <IgnoreWithExceptionsModal
      show={show}
      selectedIssuesCount={selectedIssuesCount}
      onHide={onHide}
      onIgnore={onIgnore}
      isCategoryException={false}
    />
  );
};

type IgnoreIssuesModalDetailsProps = IgnoreIssuesModalBaseProps & {
  id: number;
};

/**
 * Container component for the ignore modal for use on the IssueDetails.
 */
export const IgnoreIssuesModalDetailsContainer: VoidFunctionComponent<IgnoreIssuesModalDetailsProps> = ({
  id,
  show,
  selectedIssuesCount,
  onHide,
}) => {
  const addToast = useAddToast();
  const dispatch = useDispatch();

  const onIgnore = async (scope: IgnoreModalOnIgnoreParams) => {
    if (isIssueExceptionIgnore(scope) && !isSimpleIgnoreAction(scope)) {
      dispatch(
        IssuesV2Store.actions.createIssueExceptions(
          { issueIds: [id] },
          scope.ignoreScope,
          scope.packageScope,
          scope.expiresAfter,
          scope.notes,
          scope.resolutionReason,
        ),
      );
    } else {
      dispatch(IssuesV2Store.actions.ignoreIssues({ issueIds: [id] }, scope.notes, scope.resolutionReason));
    }
    //  This handles either closing the drawer (if on the inbox) or
    //  re-fetching the issue details (if we're on the full-page).
    dispatch(IssuesV2Store.actions.refetchIssueDetailsAfterAction(id));

    //  Hide the Ignore modal
    onHide();
    //  Display a toast letting the user know the action was successful.
    addToast({
      type: 'success',
      content: `Ignored ${pluralizeIssues(selectedIssuesCount)}.`,
    });
  };

  // Issue exceptions are limited to only licensing and vulnerability issues at the moment.
  return (
    <IgnoreWithExceptionsModal
      show={show}
      selectedIssuesCount={selectedIssuesCount}
      onHide={onHide}
      onIgnore={onIgnore}
      isCategoryException={false}
    />
  );
};

type IgnoreIssuesModalCategoryProps = Omit<IgnoreIssuesModalBaseProps, 'selectedIssuesCount'>;

/**
 * Container component for the ignore modal for use on category IssueIgnore
 */
export const IgnoreIssuesModalCategoryContainer: VoidFunctionComponent<IgnoreIssuesModalCategoryProps> = ({
  show,
  onHide,
}) => {
  const addToast = useAddToast();
  const dispatch = useDispatch();
  const issueCategory = useSelector(IssuesV2Store.selectors.getIssueCategory);
  const { fetchExceptionsData } = useIssueExceptions({
    projectId: undefined,
    releaseGroupId: undefined,
    category: issueCategory,
  });

  const onIgnore = async (scope: IgnoreModalOnIgnoreParams) => {
    try {
      if (isIssueExceptionIgnore(scope) && !isSimpleIgnoreAction(scope)) {
        await dispatch(
          IssuesV2Store.actions.createIssueExceptions(
            { issueIds: [] },
            scope.ignoreScope,
            scope.packageScope,
            scope.expiresAfter,
            scope.notes,
            scope.resolutionReason,
            scope.packageLabelIds,
          ),
        );
        await fetchExceptionsData();
      } else {
        await dispatch(IssuesV2Store.actions.ignoreIssues({ issueIds: [] }, scope.notes, scope.resolutionReason));
      }
      addToast({
        type: 'success',
        content: `Created ignore rule for ${issueCategory} issues.`,
      });
    } catch (error) {
      addToast({
        type: 'error',
        content: `Failed to create ignore rule for ${issueCategory} issues.`,
      });
    }
    //  Hide the Ignore modal
    onHide();
  };
  return <IgnoreWithExceptionsModal show={show} onHide={onHide} isCategoryException onIgnore={onIgnore} />;
};
